import clsx from 'clsx'
import { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '../../../../../../shared/lib/hooks/UseAuth'
import { useMenuLayout } from '../../../hooks/UseMenuLayout'
import { useSidebarExpanded } from '../../../hooks/UseSidebarExpanded'
import { MAIN_NAVIGATION, SERVICE_NAVIGATION } from '../Navigation.const'
import { SidebarIcon } from '../../../../../../shared/components/icons/nav/SidebarIcon'
import { Logotype } from './Logotype'
import { NavMenu } from './NavMenu'
import { NavTooltip } from './NavTooltip'
import { TariffButton } from './TariffButton'
import { ImpersonateSelect } from '../../../ui/ImpersonateSelect'

/** Элементы со своим действием: клик по ним свёрнутую панель не раскрывает */
const INTERACTIVE_SELECTOR = 'a, button, input, textarea, select, [role="button"]'

export const Sidebar = () => {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const { switchMenuLayout } = useMenuLayout()
  const { expanded, toggleSidebar } = useSidebarExpanded()

  const handleAction = (id: string) => {
    switch (id) {
      case 'logout':
        return logout()
      case 'switch-menu':
        return switchMenuLayout('top')
    }
  }

  // Свёрнутую панель раскрывает клик по свободному месту. Клик из поповера тоже доходит
  // сюда — React ведёт его через портал по своему дереву, — но в DOM панели его цели нет.
  // Сворачивается панель только кнопкой, поэтому в раскрытой клик ничего не делает
  const handlePanelClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as Element

    if (expanded || !event.currentTarget.contains(target)) return
    if (target.closest(INTERACTIVE_SELECTOR)) return

    toggleSidebar()
  }

  return (
    <div
      className={clsx(
        'flex h-full w-full flex-col gap-[8px] overflow-hidden bg-[#00326D] p-[8px] transition-all duration-150',
        {
          ['min-w-[200px] max-w-[200px]']: expanded,
          ['min-w-[60px] max-w-[60px] cursor-pointer']: !expanded,
        },
      )}
      onClick={handlePanelClick}
    >
      <div className={'group relative flex justify-between'}>
        <Link
          className={clsx('flex h-[44px] items-center justify-center', {
            ['w-[44px] transition-all duration-150 group-hover:opacity-0']: !expanded,
            ['px-[9px]']: expanded,
          })}
          to={'/projects'}
        >
          <Logotype collapsed={!expanded} />
        </Link>
        {/* В свёрнутой панели кнопка лежит поверх логотипа и проявляется при наведении */}
        <NavTooltip label={expanded ? t('navigation.collapse') : t('navigation.expand')}>
          <button
            className={clsx(
              'group/collapse-button flex h-[44px] w-[44px] items-center justify-center rounded-[8px] hover:bg-[#8D7CA71F]',
              {
                ['absolute left-0 opacity-0 transition-all duration-150 group-hover:opacity-100']:
                  !expanded,
              },
            )}
            onClick={toggleSidebar}
          >
            <span className={'text-white opacity-40 group-hover/collapse-button:opacity-100'}>
              <SidebarIcon />
            </span>
          </button>
        </NavTooltip>
      </div>
      <div
        className={clsx('pl-[14px] pr-[8px] [&_.transition-opacity.invisible]:hidden', {
          hidden: !expanded,
        })}
      >
        <ImpersonateSelect />
      </div>
      <NavMenu
        className={'scrollbar-hide flex flex-1 flex-col overflow-y-auto'}
        collapsed={!expanded}
        items={MAIN_NAVIGATION}
      />
      <NavMenu
        className={'flex flex-col'}
        collapsed={!expanded}
        items={SERVICE_NAVIGATION}
        onAction={handleAction}
      />
      <TariffButton collapsed={!expanded} />
    </div>
  )
}
